import React, { forwardRef } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FitScreenOutlinedIcon from "@mui/icons-material/FitScreenOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.1;

const InteractiveMapCanvas = forwardRef(function InteractiveMapCanvas(
  {
    width,
    height,
    zoom,
    onZoomChange,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    description,
    itemCount,
    itemLabel = "item",
    connectionCount,
    connectionLabel = "connection",
    empty = false,
    emptyState = null,
    viewportHeight = { xs: 672, md: 816 },
    children,
  },
  viewportRef
) {
  const fitCanvasToView = () => {
    const viewport = viewportRef?.current;
    if (!viewport || !width || !height) return;

    const horizontalFit = (viewport.clientWidth - 40) / width;
    const verticalFit = (viewport.clientHeight - 40) / height;
    const fittedZoom = Math.min(
      MAX_ZOOM,
      Math.max(MIN_ZOOM, Math.min(horizontalFit, verticalFit))
    );

    onZoomChange(fittedZoom);
    viewport.scrollTo({ left: 0, top: 0, behavior: "smooth" });
  };

  const countLabel = (count, singular) =>
    `${count} ${count === 1 ? singular : `${singular}s`}`;

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() =>
                  onZoomChange((current) =>
                    Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2)))
                  )
                }
                disabled={zoom <= MIN_ZOOM}
              >
                <ZoomOutOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" sx={{ minWidth: 38, textAlign: "center" }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() =>
                  onZoomChange((current) =>
                    Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2)))
                  )
                }
                disabled={zoom >= MAX_ZOOM}
              >
                <ZoomInOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Fit map to view">
            <IconButton size="small" onClick={fitCanvasToView}>
              <FitScreenOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {itemCount != null && (
            <Chip size="small" label={countLabel(itemCount, itemLabel)} />
          )}
          {connectionCount != null && (
            <Chip
              size="small"
              label={countLabel(connectionCount, connectionLabel)}
            />
          )}
        </Stack>
      </Box>

      <Box
        ref={viewportRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        sx={{
          height: viewportHeight,
          overflow: "auto",
          position: "relative",
          bgcolor: "background.default",
        }}
      >
        {empty ? (
          <Box
            sx={{
              position: "sticky",
              left: 0,
              top: 0,
              minHeight: 560,
              display: "grid",
              placeItems: "center",
              p: 3,
              textAlign: "center",
            }}
          >
            {emptyState}
          </Box>
        ) : (
          <Box
            sx={{
              width: width * zoom,
              height: height * zoom,
              position: "relative",
            }}
          >
            <Box
              sx={{
                width,
                height,
                position: "absolute",
                left: 0,
                top: 0,
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
                backgroundImage:
                  "radial-gradient(currentColor 0.7px, transparent 0.7px)",
                backgroundSize: "22px 22px",
                color: "text.disabled",
              }}
            >
              {children}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
});

export default InteractiveMapCanvas;
